import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Chrome, Download, CheckCircle2, AlertCircle, Zap } from 'lucide-react';

interface ChromeExtensionPromptProps {
  variant?: 'compact' | 'full';
  showStatus?: boolean;
}

export function ChromeExtensionPrompt({ variant = 'full', showStatus = true }: ChromeExtensionPromptProps) {
  // In a real implementation, this would check if the extension is installed
  // For now, we'll show a prompt to install
  const isExtensionInstalled = false;

  const handleInstallExtension = () => {
    // Link to Chrome Web Store (would be your actual extension URL)
    window.open('https://chrome.google.com/webstore', '_blank');
  };

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="bg-primary/20 p-2 rounded-lg">
            <Zap className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm mb-1 flex items-center gap-2">
              Automate Applications with Chrome Extension
              {isExtensionInstalled ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Installed
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/20">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Not Installed
                </Badge>
              )}
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              {isExtensionInstalled 
                ? 'Your extension is ready to auto-fill applications!'
                : 'Install our Chrome extension to automatically fill out job applications with your AI-generated materials.'
              }
            </p>
            {!isExtensionInstalled && (
              <Button size="sm" onClick={handleInstallExtension} className="gap-2">
                <Chrome className="h-4 w-4" />
                Install Extension
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 border-primary/20">
      <div className="flex items-start gap-4">
        <div className="bg-primary/20 p-3 rounded-xl">
          <Chrome className="h-8 w-8 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-semibold">Chrome Extension Auto-Apply</h3>
            {showStatus && (
              isExtensionInstalled ? (
                <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30">
                  <Download className="h-3 w-3 mr-1" />
                  Setup Required
                </Badge>
              )
            )}
          </div>

          {isExtensionInstalled ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                ✅ Your Chrome extension is installed and ready! When you click "Auto-Apply", your materials will be automatically filled into the job application form.
              </p>
              <div className="bg-background/50 rounded-lg p-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">How it works:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• AI generates custom resume, cover letter, and form data</li>
                  <li>• Extension detects application forms automatically</li>
                  <li>• Fields are filled intelligently based on job requirements</li>
                  <li>• You review and submit with one click</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Install our Chrome extension to unlock <strong>one-click auto-apply</strong>. The extension will automatically fill out job applications using your AI-generated materials.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="bg-background/50 rounded-lg p-3">
                  <div className="font-medium mb-1 flex items-center gap-1">
                    <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                    Install Extension
                  </div>
                  <p className="text-muted-foreground">Add our Chrome extension from the Web Store</p>
                </div>
                <div className="bg-background/50 rounded-lg p-3">
                  <div className="font-medium mb-1 flex items-center gap-1">
                    <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                    Queue Jobs
                  </div>
                  <p className="text-muted-foreground">AI prepares custom materials for each job</p>
                </div>
                <div className="bg-background/50 rounded-lg p-3">
                  <div className="font-medium mb-1 flex items-center gap-1">
                    <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                    Auto-Apply
                  </div>
                  <p className="text-muted-foreground">Extension fills forms automatically</p>
                </div>
              </div>

              <Button onClick={handleInstallExtension} className="gap-2 w-full sm:w-auto">
                <Chrome className="h-4 w-4" />
                Install Chrome Extension
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
